// the module, not the custodial barrel: only this chain budget is wanted, and
// the barrel pulls the whole deposit/withdraw feature in behind it.
import { CRONOS_TX_ESTIMATE_MS } from '@/features/custodial/operationPhase';

import type { EoaProvisioningPhase } from './eoaProvisioningCeremony';

/**
 * Time estimates for the account-creation progress bars.
 *
 * Neither leg reports real progress: both are waiting on service-wallet
 * transactions to be included, so the bar is a clock against the chain's own
 * budget, reusing the same per-chain figures the deposit/withdraw bars run on
 * (`features/custodial/operationPhase`).
 *
 * A phase that is waiting on the USER (a wallet signature) gets no estimate at
 * all. Running a countdown against someone deciding whether to press Confirm
 * invents a deadline that does not exist, and makes a normal pause look like a
 * stall.
 */

/**
 * Cronos service-wallet transactions the authorization leg costs.
 *
 * Exactly one: `createWalletFor` deploys the authorization wallet, registers the
 * account-hash set and binds the owner in a single call (MoonHub.API
 * `CustodialWalletEngine.provisionAuthorizationWallet`). Everything else on that
 * leg is a signature or an eth_call.
 */
export const CRONOS_PROVISIONING_TX_COUNT = 1;

/** The Cronos transaction, at the ~6s-block budget of one Cronos operation. */
export const CRONOS_PROVISIONING_ESTIMATE_MS =
    CRONOS_TX_ESTIMATE_MS * CRONOS_PROVISIONING_TX_COUNT;

/**
 * The HyperEVM leg is more than its ~1s block: the backend signs the association
 * with RustSigner, ranks the service wallets, signs and broadcasts the wallet +
 * adapter creation, polls for the receipt and reads the wallet back before it
 * marks the queue entry complete, which this page then learns on its next poll.
 * Measured 2026-09-03 on a Robinhood-anchored onboarding: provisioning started
 * to completed was ~11.6s server-side. Budgeted a little under that because the
 * bar only ever claims 95% and completion, not the clock, fills it.
 */
export const HYPEREVM_PROVISIONING_ESTIMATE_MS = 9_000;

/** Never claims completion: the chain decides when the leg ends, not the clock. */
const MAX_ESTIMATED_PERCENT = 95;

/**
 * What the bar is timing right now, or null when nothing should be shown.
 *
 * `detached` covers the stretch where the backend owns the HyperEVM creation and
 * the ceremony has already returned, including a reload that arrives with the
 * queue mid-run and no local phase at all.
 */
export function provisioningLeg(
    phase: EoaProvisioningPhase | null,
    detached: boolean,
): 'anchor' | 'hyperevm' | null {
    if (phase === 'anchor-provisioning') return 'anchor';
    if (phase === 'hyperevm-provisioning') return 'hyperevm';
    // a signing phase is blocked on the user, so it times nothing
    if (phase) return null;
    return detached ? 'hyperevm' : null;
}

/** Estimated duration of the running leg, or null when no bar belongs here. */
export function provisioningEstimateMs(
    phase: EoaProvisioningPhase | null,
    detached: boolean,
): number | null {
    const leg = provisioningLeg(phase, detached);
    if (!leg) return null;
    return leg === 'anchor'
        ? CRONOS_PROVISIONING_ESTIMATE_MS
        : HYPEREVM_PROVISIONING_ESTIMATE_MS;
}

/**
 * The wait each leg is worth telling the user about.
 *
 * Deliberately a range rather than the estimate read back as a number: the
 * estimate is the bar's budget, while these are what the legs actually take when
 * the block pool is busy. The HyperEVM wording matches the queue step, and the
 * stall timeout that re-arms the button is set against it.
 */
export const PROVISIONING_HINT: Record<'anchor' | 'hyperevm', string> = {
    anchor: 'This usually takes a few seconds.',
    hyperevm: 'This usually takes a few seconds.',
};

export interface ProvisioningProgressEstimate {
    percent: number;
    secondsElapsed: number;
    /** Ran past the estimate: the bar can no longer predict, and says so. */
    overrun: boolean;
}

/**
 * Progress of the running leg against its estimate.
 *
 * Caps just short of full, because a bar sitting at 100% while the spinner keeps
 * turning reads as a hang. Only the leg actually landing fills it, which the
 * caller signals with `complete`.
 */
export function provisioningProgress(
    startedAtMs: number | null,
    nowMs: number,
    estimateMs: number | null,
    options: { complete?: boolean } = {},
): ProvisioningProgressEstimate {
    if (options.complete) {
        return { percent: 100, secondsElapsed: 0, overrun: false };
    }
    if (startedAtMs === null || !Number.isFinite(startedAtMs) || !estimateMs) {
        return { percent: 0, secondsElapsed: 0, overrun: false };
    }
    // a clock skew must never render a negative bar
    const elapsed = Math.max(0, nowMs - startedAtMs);
    return {
        percent: Math.min(MAX_ESTIMATED_PERCENT, (elapsed / estimateMs) * 100),
        secondsElapsed: Math.floor(elapsed / 1_000),
        overrun: elapsed > estimateMs,
    };
}
